/*
 * Copyright 2026 Sony Corporation
 */

import { createTheme } from '@mui/material';

/**
 * VS Code Dark+, near enough to be familiar.
 *
 * The colours are taken from the editor's own theme rather than invented, so
 * the app sits next to it without clashing. A 3D viewport is the real subject
 * here: a dark shell keeps the eye on the render instead of on the chrome.
 */
const VSCODE = {
  editor: '#1e1e1e',
  sideBar: '#252526',
  border: '#3c3c3c',
  input: '#3c3c3c',
  foreground: '#cccccc',
  description: '#9d9d9d',
  disabled: '#6a6a6a',
  link: '#3794ff',
  selection: '#04395e',
  hover: '#2a2d2e',
  green: '#89d185',
  yellow: '#cca700',
  red: '#f14c4c',
  // Translucent, as the editor's are: they darken whatever they sit over
  // rather than introducing a third surface colour.
  scrollbar: '#79797966',
  scrollbarHover: '#646464b3',
} as const;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: VSCODE.link },
    background: { default: VSCODE.editor, paper: VSCODE.sideBar },
    divider: VSCODE.border,
    text: { primary: VSCODE.foreground, secondary: VSCODE.description, disabled: VSCODE.disabled },
    success: { main: VSCODE.green },
    warning: { main: VSCODE.yellow },
    error: { main: VSCODE.red },
  },
  typography: {
    fontFamily: 'Roboto, system-ui, sans-serif',
    // MUI sizes everything in rem as `px / htmlFontSize`. The document root is
    // the usual 16px, so quoting 16/0.9 renders every MUI font and icon at 90%
    // without touching Tailwind's rem-based spacing.
    htmlFontSize: 16 / 0.9,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${VSCODE.scrollbar} transparent`,
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: VSCODE.scrollbar,
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: VSCODE.scrollbarHover },
        '*::-webkit-scrollbar-corner': { background: 'transparent' },
      },
    },
    // MUI lightens every dark surface by elevation. VS Code's panels are flat
    // and distinguished by their own colour, so the overlay only muddies them.
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    // The dense toolbar's 48px is still generous for one row of small controls.
    // The media query is the one MUI sets the variant with, so it has to be
    // overridden too or it wins back above 600px.
    MuiToolbar: {
      styleOverrides: {
        dense: { minHeight: 38, '@media (min-width:600px)': { minHeight: 38 } },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: VSCODE.editor,
          backgroundImage: 'none',
          borderBottom: `1px solid ${VSCODE.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: VSCODE.hover },
          '&.Mui-selected': { backgroundColor: VSCODE.selection },
          '&.Mui-selected:hover': { backgroundColor: VSCODE.selection },
        },
      },
    },
    MuiOutlinedInput: { styleOverrides: { root: { backgroundColor: VSCODE.input } } },
    MuiAccordion: { styleOverrides: { root: { backgroundColor: 'transparent' } } },
  },
});
